// updateLanguage.js
'use strict';
import { replace as historyReplace } from 'redux-first-history';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import removeShopCurrency from '../action/removeShopCurrency.js';
import i18n, { localeConvertor } from '../resource/i18n.js';
import { setRemoteMeta } from '../resource/mixpanel.js';
import { getSupportedLanguage } from '../resource/getCurrentLanguage.js';
import { removeLangFromPath, generateLangPath } from '../resource/langPath.js';
import {
  CONFIG_PRIORITY_PRESENCE_CLIENT,
  CONFIG_PRIORITY_PRESENCE_USER,
} from '../resource/configPriority.js';
import getMeData from '../selector/getMeData.js';
import getOperationData from '../selector/getOperationData.js';
import getRouterData from '../selector/getRouterData.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import { sendMessageToSW } from '../serviceWorker/helpers.js';
import postMessageToApp from '../action/postMessageToApp.js';
import fetchConfigurationsBootstrap from '../action/fetchConfigurationsBootstrap.js';

/**
 * Update language (both i18n.language and redux)
 * @kind action
 * @param {string} {language} - action param.
 * @return {Promise} Action promise.
 */
const updateLanguage =
  ({ language }) =>
  async (dispatch, getState) => {
    const lang = getSupportedLanguage({
      language,
      supportedLanguages: getSupportedLanguages(getState()),
    });

    i18n.changeLanguage(lang, error => {
      if (error) {
        return;
      }

      const pathname = getRouterData(getState(), 'location.pathname');
      const currentPath = removeLangFromPath(pathname);
      const newPath = generateLangPath(currentPath, lang);

      if (pathname !== newPath) {
        const search = getRouterData(getState(), 'location.search') || '';
        const hash = getRouterData(getState(), 'location.hash') || '';
        const state = getRouterData(getState(), 'location.state');
        dispatch(historyReplace(newPath + search + hash, state));
      }

      setRemoteMeta({ data: {} });
      dispatch(
        postMessageToApp({
          message: 'onLanguageTagUpdate',
          messageArgs: [lang],
        })
      );
    });

    const usingShopId = getOperationData(getState(), ['shop'], 'usingShopId');
    // Don't remove currency if have `usingShopId`.
    // Remove currency break product detail UI, since there are some value need currency.
    if (!usingShopId) {
      dispatch(removeShopCurrency());
    }

    sendMessageToSW({
      type: MessageTypes.SET_AUTHORIZER_LANGUAGE,
      payload: {
        language: localeConvertor({ locale: language, isISO639: true }),
      },
    });
    // Re-fetch both priorities, as the app boot does: the merged remote config
    // lets the user priority win over the client one, so refreshing only the
    // client bootstrap would leave a logged-in user's language-keyed config
    // groups on the previous language. The user bootstrap also re-applies the
    // GrowthBook `language` attribute, and no-ops while logged out.
    [CONFIG_PRIORITY_PRESENCE_CLIENT, CONFIG_PRIORITY_PRESENCE_USER].forEach(
      priority =>
        dispatch(fetchConfigurationsBootstrap({ priority, language: lang }))
    );
    // re-subscribe private-enc-user-v2@{USER_ID}
    const meId = getMeData(getState(), 'id');
    sendMessageToSW({
      type: MessageTypes.SUBSCRIBE_PRIVATE_USER_V2_CHANNEL,
      payload: {
        userId: meId,
      },
    });
    return dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['config'],
        data: {
          language: lang,
        },
      },
    });
  };

export default updateLanguage;
